import React, { useState, useMemo } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Box, Typography, Divider, Alert, FormControl, InputLabel, Select, MenuItem, TextField, Stack, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { WorkoutForm } from './WorkoutForm';
import { Exercise } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';
import { ExerciseAutocomplete } from '../common/ExerciseAutocomplete';

export interface WorkoutEntryFormState {
  exercise: string;
  comment: string;
  details: { id?: number; series: string; reps: string; weight: string; rest_time_seconds?: string; rir?: string }[];
  workout_type: 'traditional' | 'amrap';
  amrap_time_minutes?: string;
  amrap_details?: { exercise_id: string; exercise_name?: string; reps: string }[];
}

interface ValidationError {
  message: string;
  field: string;
  detailIndex?: number;
}

interface WorkoutEntryDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  exercises: Exercise[];
  initialFormValues: WorkoutEntryFormState;
  onFormChange: (field: keyof WorkoutEntryFormState, value: any) => void;
  onSubmit: () => void;
  isSavingSet: boolean;
  onCreateExercise?: (name: string) => Promise<Exercise | null>;
}

export const WorkoutEntryDialog: React.FC<WorkoutEntryDialogProps> = ({
  open,
  onClose,
  mode,
  exercises,
  initialFormValues,
  onFormChange,
  onSubmit,
  isSavingSet,
  onCreateExercise,
}) => {
  const { t } = useLanguage();
  const [activeDetailIndex, setActiveDetailIndex] = useState(0);
  const [showValidation, setShowValidation] = useState(false);

  const dialogTitle = mode === 'add' ? t('calendar.addWorkoutEntryTitle') : t('calendar.editWorkoutEntryTitle');
  const submitButtonText = mode === 'add' ? t('workout.add') : t('workout.save');

  // Validation logic
  const validationErrors = useMemo((): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (initialFormValues.workout_type === 'traditional') {
      // Validate exercise selection for traditional workouts
      if (!initialFormValues.exercise || initialFormValues.exercise.trim() === '') {
        errors.push({
          message: t('workout.validation.exerciseRequired'),
          field: 'exercise'
        });
      }

      // Validate each detail for traditional workouts
      initialFormValues.details.forEach((detail, index) => {
        // Validate required fields
        if (!detail.series || detail.series.trim() === '' || isNaN(Number(detail.series)) || Number(detail.series) <= 0) {
          errors.push({
            message: t('workout.validation.seriesRequired'),
            field: 'series',
            detailIndex: index
          });
        }

        if (!detail.reps || detail.reps.trim() === '' || isNaN(Number(detail.reps)) || Number(detail.reps) <= 0) {
          errors.push({
            message: t('workout.validation.repsRequired'),
            field: 'reps',
            detailIndex: index
          });
        }

        if (!detail.weight || detail.weight.trim() === '' || isNaN(Number(detail.weight)) || Number(detail.weight) < 0) {
          errors.push({
            message: t('workout.validation.weightRequired'),
            field: 'weight',
            detailIndex: index
          });
        }

        // Validate optional fields
        if (detail.rest_time_seconds && detail.rest_time_seconds.trim() !== '') {
          const restTime = Number(detail.rest_time_seconds);
          if (isNaN(restTime) || restTime < 0) {
            errors.push({
              message: t('workout.validation.restTimeInvalid'),
              field: 'rest_time_seconds',
              detailIndex: index
            });
          }
        }

        if (detail.rir && detail.rir.trim() !== '') {
          const rir = Number(detail.rir);
          if (isNaN(rir) || rir < 0 || rir > 10) {
            errors.push({
              message: t('workout.validation.rirInvalid'),
              field: 'rir',
              detailIndex: index
            });
          }
        }
      });
    } else if (initialFormValues.workout_type === 'amrap') {
      // Validate AMRAP time
      if (!initialFormValues.amrap_time_minutes || initialFormValues.amrap_time_minutes.trim() === '' || 
          isNaN(Number(initialFormValues.amrap_time_minutes)) || Number(initialFormValues.amrap_time_minutes) <= 0) {
        errors.push({
          message: 'Time is required and must be greater than 0',
          field: 'amrap_time_minutes'
        });
      }

      // Validate AMRAP exercises
      if (!initialFormValues.amrap_details || initialFormValues.amrap_details.length === 0) {
        errors.push({
          message: 'At least one exercise is required',
          field: 'amrap_details'
        });
      } else {
        initialFormValues.amrap_details.forEach((amrapDetail, index) => {
          if (!amrapDetail.exercise_id || amrapDetail.exercise_id.trim() === '') {
            errors.push({
              message: `Exercise selection is required for exercise ${index + 1}`,
              field: 'amrap_exercise',
              detailIndex: index
            });
          }

          if (!amrapDetail.reps || amrapDetail.reps.trim() === '' || 
              isNaN(Number(amrapDetail.reps)) || Number(amrapDetail.reps) <= 0) {
            errors.push({
              message: `Reps are required and must be greater than 0 for exercise ${index + 1}`,
              field: 'amrap_reps',
              detailIndex: index
            });
          }
        });
      }
    }

    return errors;
  }, [initialFormValues, t]);

  const isFormValid = validationErrors.length === 0;

  const getFieldError = (field: string, detailIndex?: number): string | undefined => {
    if (!showValidation) return undefined;
    const error = validationErrors.find(e => e.field === field && e.detailIndex === detailIndex);
    return error?.message;
  };

  const hasFieldError = (field: string, detailIndex?: number): boolean => {
    if (!showValidation) return false;
    return validationErrors.some(e => e.field === field && e.detailIndex === detailIndex);
  };

  const handleDetailChange = (index: number, field: 'series' | 'reps' | 'weight' | 'rest_time_seconds' | 'rir', value: string) => {
    const newDetails = [...initialFormValues.details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    onFormChange('details', newDetails);
  };

  const handleAddDetail = () => {
    const newDetails = [...initialFormValues.details, { series: '', reps: '', weight: '', rest_time_seconds: '', rir: '' }];
    onFormChange('details', newDetails);
    setActiveDetailIndex(newDetails.length - 1);
  };

  const handleRemoveDetail = (index: number) => {
    if (initialFormValues.details.length > 1) {
      const newDetails = initialFormValues.details.filter((_, i) => i !== index);
      onFormChange('details', newDetails);
      setActiveDetailIndex(Math.max(0, index - 1));
    }
  };

  const handleWorkoutTypeChange = (newType: 'traditional' | 'amrap') => {
    if (newType === 'amrap' && (!initialFormValues.amrap_details || initialFormValues.amrap_details.length === 0)) {
      // Initialize with one empty exercise when switching to AMRAP
      onFormChange('amrap_details', [{ exercise_id: '', reps: '' }]);
    }
    onFormChange('workout_type', newType);
  };

  const handleWorkoutFormSubmit = () => {
    setShowValidation(true);
    if (isFormValid) {
      onSubmit();
    }
  };

  // Reset validation when dialog opens/closes
  const handleClose = () => {
    setShowValidation(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" sx={commonDialogStyles}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {showValidation && validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('workout.validation.title')}:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, index) => (
                <li key={index}>
                  {error.detailIndex !== undefined 
                    ? `${t('workout.detailNumber', { number: error.detailIndex + 1 })}: ${error.message}`
                    : error.message
                  }
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Workout Type Selector */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('workout.workoutType')}</InputLabel>
          <Select
            value={initialFormValues.workout_type}
            label={t('workout.workoutType')}
            onChange={(e) => handleWorkoutTypeChange(e.target.value as 'traditional' | 'amrap')}
          >
            <MenuItem value="traditional">{t('workout.traditionalType')}</MenuItem>
            <MenuItem value="amrap">{t('workout.amrapType')}</MenuItem>
          </Select>
        </FormControl>

        {initialFormValues.workout_type === 'traditional' ? (
          <>
            <WorkoutForm
              exercises={exercises}
              exercise={initialFormValues.exercise}
              comment={initialFormValues.comment}
              setExercise={(value) => onFormChange('exercise', value)}
              setComment={(value) => onFormChange('comment', value)}
              onCreateExercise={onCreateExercise}
              hasError={hasFieldError('exercise')}
              errorMessage={getFieldError('exercise')}
            />
          </>
        ) : (
          <>
            {/* AMRAP Form */}
            <Box sx={{ mb: 2 }}>
              <TextField
                label={t('workout.comment')}
                value={initialFormValues.comment}
                onChange={(e) => onFormChange('comment', e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder={t('workout.amrapCommentPlaceholder')}
                sx={{ mb: 2 }}
              />
              
              <TextField
                label={t('workout.time')}
                type="number"
                value={initialFormValues.amrap_time_minutes || ''}
                onChange={(e) => onFormChange('amrap_time_minutes', e.target.value)}
                fullWidth
                placeholder={t('workout.timePlaceholder')}
                sx={{ mb: 2 }}
                error={hasFieldError('amrap_time_minutes')}
                helperText={getFieldError('amrap_time_minutes')}
              />
            </Box>
          </>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        {initialFormValues.workout_type === 'traditional' ? (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('workout.details')}</Typography>
            <Divider sx={{ mb: 2 }} />
            
            {initialFormValues.details.map((detail, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: index === activeDetailIndex ? '1px solid #1976d2' : '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">{t('workout.detailNumber', { number: index + 1 })}</Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveDetail(index)}
                    disabled={initialFormValues.details.length <= 1}
                  >
                    {t('workout.remove')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <WorkoutForm.DetailFields
                    series={detail.series}
                    reps={detail.reps}
                    weight={detail.weight}
                    rest_time_seconds={detail.rest_time_seconds}
                    rir={detail.rir}
                    setSeries={(value) => handleDetailChange(index, 'series', value)}
                    setReps={(value) => handleDetailChange(index, 'reps', value)}
                    setWeight={(value) => handleDetailChange(index, 'weight', value)}
                    setRestTimeSeconds={(value) => handleDetailChange(index, 'rest_time_seconds', value)}
                    setRir={(value) => handleDetailChange(index, 'rir', value)}
                    onClick={() => setActiveDetailIndex(index)}
                    validationErrors={{
                      series: hasFieldError('series', index) ? getFieldError('series', index) : undefined,
                      reps: hasFieldError('reps', index) ? getFieldError('reps', index) : undefined,
                      weight: hasFieldError('weight', index) ? getFieldError('weight', index) : undefined,
                      rest_time_seconds: hasFieldError('rest_time_seconds', index) ? getFieldError('rest_time_seconds', index) : undefined,
                      rir: hasFieldError('rir', index) ? getFieldError('rir', index) : undefined,
                    }}
                  />
                </Box>
              </Box>
            ))}
            
            <Button
              variant="outlined"
              fullWidth 
              onClick={handleAddDetail}
              sx={{ mt: 1 }}
            >
              {t('workout.addDetail')}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('workout.amrapExercises')}</Typography>
            <Divider sx={{ mb: 2 }} />
            
            {(initialFormValues.amrap_details || []).map((amrapDetail, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">{t('workout.exerciseNumber')} {index + 1}</Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newAmrapDetails = (initialFormValues.amrap_details || []).filter((_, i) => i !== index);
                      onFormChange('amrap_details', newAmrapDetails);
                    }}
                    disabled={(initialFormValues.amrap_details || []).length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
                  <Box sx={{ flex: 1 }}>
                    <ExerciseAutocomplete
                      exercises={exercises}
                      value={exercises.find(ex => ex.id.toString() === amrapDetail.exercise_id) || null}
                      onChange={(selectedExercise) => {
                        const newAmrapDetails = [...(initialFormValues.amrap_details || [])];
                        newAmrapDetails[index] = {
                          ...newAmrapDetails[index],
                          exercise_id: selectedExercise ? selectedExercise.id.toString() : '',
                          exercise_name: selectedExercise ? selectedExercise.name : ''
                        };
                        onFormChange('amrap_details', newAmrapDetails);
                      }}
                      onCreateExercise={onCreateExercise}
                      label={t('workout.exercise')}
                      error={hasFieldError('amrap_exercise', index)}
                      helperText={getFieldError('amrap_exercise', index)}
                    />
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '120px' } }}>
                    <TextField
                      label={t('workout.totalReps')}
                      type="number"
                      value={amrapDetail.reps}
                      onChange={(e) => {
                        const newAmrapDetails = [...(initialFormValues.amrap_details || [])];
                        newAmrapDetails[index] = { ...newAmrapDetails[index], reps: e.target.value };
                        onFormChange('amrap_details', newAmrapDetails);
                      }}
                      fullWidth
                      error={hasFieldError('amrap_reps', index)}
                      helperText={getFieldError('amrap_reps', index)}
                    />
                  </Box>
                </Stack>
              </Box>
            ))}
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                const newAmrapDetails = [...(initialFormValues.amrap_details || []), { exercise_id: '', reps: '' }];
                onFormChange('amrap_details', newAmrapDetails);
              }}
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
            >
              {t('workout.addExercise')}
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSavingSet}>{t('workout.cancel')}</Button>
        <Button
          onClick={handleWorkoutFormSubmit}
          color="primary" 
          variant="contained"
          disabled={isSavingSet}
          startIcon={isSavingSet ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 